import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import React from 'react';
import { Theme, ThemeProps } from '@theme/base';
import { Icons } from '@model/common/icons';
import { getPaceLabel, getPaceDescription } from '@util/tours';
import { TestId } from '@components/test-ids';
import { useI18NextContext } from '@components/hooks';
import { Icon } from '@components/common/generic-icon';
import { SignalIcon } from '@components/tours';
import { ToolTip } from '@components/common/tooltip';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TourProductDescriptionProps {
  theme?: Theme;
  testId?: string;
  name?: string;
  subTitle?: string;
  country?: string;
  continent?: string;
  pace?: number;
  mealsCount?: number;
  excursionsCount?: number;
  subPanel?: boolean;
  isCondensed?: boolean;
}

/* ***************** *
 *       Styles      *
 * ***************** */
interface ContainerProps extends ThemeProps {
  isCondensed?: boolean;
}
const Container: any = styled.div(({ theme, isCondensed }: ContainerProps) => ({
  ...(!isCondensed && {
    paddingBottom: theme.custom.spacing.xLarge,
    borderBottom: `1px dashed ${theme.custom.colors.group4.lighter}`,
    marginBottom: theme.custom.spacing.large
  })
}));

interface TitleProps extends ThemeProps {
  subPanel?: boolean;
}
const Title: any = styled.h1(({ theme, subPanel }: TitleProps) => ({
  ...(theme.custom.typography[subPanel ? 'h4' : 'h2'] as any),
  fontWeight: 'normal',
  margin: 0
}));

const SubTitle: any = styled.h4(({ theme, subPanel }: TitleProps) => ({
  ...(theme.custom.typography[subPanel ? 'h5' : 'h4'] as any),
  fontWeight: 'normal',
  color: theme.custom.colors.group4.base,
  marginTop: 0
}));

const IconsRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap'
});

const IconLabel = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.custom.colors.group4.light,
  ...(theme.custom.typography.paragraph as any),
  marginRight: theme.custom.spacing.large,
  whiteSpace: 'nowrap',
  marginBottom: 3,

  ['i']: {
    fontSize: '1.4rem',
    marginRight: theme.custom.spacing.xSmall,
    color: theme.custom.colors.group4.base
  }
}));

const InfoIcon = styled.div(({ theme }: ThemeProps) => ({
  ['i']: {
    color: theme.custom.colors.group1.base,
    marginLeft: theme.custom.spacing.xSmall,
    cursor: 'pointer'
  }
}));

const PaceIcon = styled.span(({ theme }: ThemeProps) => ({
  marginRight: theme.custom.spacing.xSmall
}));

const PaceDescription = styled.div({
  maxWidth: 500
});

export const AttributeLabel = styled.span(({ theme }: ThemeProps) => ({
  paddingLeft: theme.custom.spacing.xSmall,
  whiteSpace: 'nowrap'
}));

/* ************************************* *
 *    TourProductDescriptionComponent    *
 * ************************************* */

export const TourProductDescriptionComponent = ({
  name,
  subTitle,
  country,
  continent,
  pace,
  mealsCount,
  excursionsCount,
  testId,
  subPanel,
  isCondensed
}: TourProductDescriptionProps) => {
  const t: any = useI18NextContext();
  return (
    <Container data-testid={testId || TestId.tourProductDescription.main} isCondensed={isCondensed}>
      <Title data-testid={TestId.tourProductDescription.title} subPanel={subPanel}>
        {name}
      </Title>
      <SubTitle data-testid={TestId.tourProductDescription.subTitle} subPanel={subPanel}>
        {subTitle}
      </SubTitle>
      <IconsRow>
        {country && continent && (
          <IconLabel data-testid={TestId.tourProductDescription.locationLabel}>
            <Icon name={Icons.MAP_PIN_OUTLINE} /> {country}, {continent}
          </IconLabel>
        )}
        {!!mealsCount && (
          <IconLabel data-testid={TestId.tourProductDescription.mealsCountLabel}>
            <Icon name={Icons.MEAL} /> {t('tour__product--meals-included-count', { count: mealsCount })}
          </IconLabel>
        )}
        {!!excursionsCount && (
          <IconLabel data-testid={TestId.tourProductDescription.excursionsCountLabel}>
            <Icon name={Icons.EYE} /> {excursionsCount} {t('common__label--excursions')}
          </IconLabel>
        )}
        {!!pace && getPaceLabel(pace) && (
          <IconLabel data-testid={TestId.tourProductDescription.paceLabel}>
            <PaceIcon>
              <SignalIcon pace={pace} />
            </PaceIcon>
            {t(getPaceLabel(pace))}
            <ToolTip content={<PaceDescription>{t(getPaceDescription(pace))}</PaceDescription>} distance={10}>
              <InfoIcon>
                <Icon name={Icons.INFO} />
              </InfoIcon>
            </ToolTip>
          </IconLabel>
        )}
      </IconsRow>
    </Container>
  );
};

export const TourProductDescription = withTheme(TourProductDescriptionComponent);
